/**
 * DimensionCooccurrence - Co-occurring root causes by dimension
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../../../../ui/card';
import { Skeleton } from '../../../../../ui/skeleton';
import { cn } from '../../../../../../lib/utils';
import type { DimensionCooccurrenceProps } from '../../types';
import { DimensionColumn } from '../molecules';

export function DimensionCooccurrence({
  data,
  onDimensionClick,
  onCauseClick,
  activeDimensions = [],
  isLoading,
  isError,
  className,
}: DimensionCooccurrenceProps) {
  if (isError) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Co-occurring Root Causes by Dimension</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-destructive">
            Failed to load dimension data
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} aria-busy="true">
        <CardHeader>
          <Skeleton className="h-6 w-64" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.dimensions.length === 0) {
    return (
      <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)}>
        <CardHeader>
          <CardTitle>Co-occurring Root Causes by Dimension</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-muted-foreground">
            No dimension co-occurrence data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('overflow-hidden rounded-[24px] border-border/70 bg-background/90 shadow-[var(--shadow-card)] backdrop-blur-sm', className)} data-testid="dimension-cooccurrence">
      <CardHeader>
        <CardTitle>Co-occurring Root Causes by Dimension</CardTitle>
        <p className="text-sm text-muted-foreground">
          Which root causes most often appear alongside each conduct dimension?{' '}
          <span className="italic">Click to toggle dimension filters.</span>
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {data.dimensions.map((dimension) => (
            <DimensionColumn
              key={dimension.dimension}
              data={dimension}
              onClick={() => onDimensionClick(dimension.dimension)}
              onCauseClick={onCauseClick}
              isActive={activeDimensions.includes(dimension.dimension)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
